import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';

import { DISPLAY_DATE_FORMAT, toDate, toDayjs } from 'helper/DateLib';

import { DataGridDateChip } from './DataGridDateChip';

const DEFAULT_MIN_DATE = new Date();

const styles = {
  wrapper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingBlock: 2,
  },
};

const setRef = (currentRef, node) => {
  if (!currentRef) {
    return;
  }

  if (typeof currentRef === 'function') {
    currentRef(node);
  } else {
    // eslint-disable-next-line no-param-reassign
    currentRef.current = node;
  }
};

const DateChipField = (props) => {
  const {
    ref,
    isPendingRow = false,
    dateValue = null,
    status,
    setOpen = () => {},
    setAnchorEl = () => {},
    InputProps: { ref: anchorRef } = {},
  } = props;

  const handleRef = (node) => {
    setRef(ref, node);
    setRef(anchorRef, node);
    setAnchorEl(node);
  };

  return (
    <DataGridDateChip
      ref={handleRef}
      isPendingRow={isPendingRow}
      dateValue={dateValue}
      status={status}
      onClick={() => setOpen((prev) => !prev)}
    />
  );
};

DateChipField.propTypes = {
  isPendingRow: PropTypes.bool,
  dateValue: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  status: PropTypes.string,
  setOpen: PropTypes.func,
  setAnchorEl: PropTypes.func,
  InputProps: PropTypes.shape({
    ref: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  }),
};

export const DataGridDatePicker = ({
  isPendingRow = false,
  params,
  minDate = DEFAULT_MIN_DATE,
  dateFormat = DISPLAY_DATE_FORMAT,
  popperPlacement = 'bottom',
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    const preventScroll = (e) => {
      if (e.target.closest('.MuiPickerPopper-root')) {
        return;
      }

      e.preventDefault();
    };

    document.addEventListener('wheel', preventScroll, { passive: false });
    document.addEventListener('touchmove', preventScroll, { passive: false });

    return () => {
      document.removeEventListener('wheel', preventScroll);
      document.removeEventListener('touchmove', preventScroll);
    };
  }, []);

  const handleChange = (value) => {
    params.api.setEditCellValue({
      id: params.id,
      field: params.field,
      value: toDate(value),
    });
  };

  const handleClose = () => {
    setIsOpen(false);
    params.api.stopCellEditMode({ id: params.id, field: params.field });
  };

  return (
    <Box onClick={(e) => e.stopPropagation()} sx={styles.wrapper}>
      <DesktopDatePicker
        open={isOpen}
        format={dateFormat}
        value={toDayjs(params.value)}
        minDate={toDayjs(minDate)}
        slots={{ field: DateChipField }}
        slotProps={{
          field: {
            isPendingRow,
            dateValue: params.value,
            status: params.row?.status,
            setOpen: setIsOpen,
            setAnchorEl,
          },
          popper: {
            anchorEl,
            placement: popperPlacement,
          },
        }}
        onChange={handleChange}
        onClose={handleClose}
      />
    </Box>
  );
};

DataGridDatePicker.propTypes = {
  isPendingRow: PropTypes.bool,
  params: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    field: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    row: PropTypes.shape({
      status: PropTypes.string,
    }),
    api: PropTypes.shape({
      setEditCellValue: PropTypes.func.isRequired,
      stopCellEditMode: PropTypes.func.isRequired,
    }).isRequired,
  }).isRequired,
  minDate: PropTypes.instanceOf(Date),
  dateFormat: PropTypes.string,
  popperPlacement: PropTypes.string,
};
